import * as React from "react";
import * as _ from "lodash";
import { Redirect } from "react-router";
import { useMappedState } from "redux-react-hook";
import styled from "styled-components";

import Grid from "src/library/components/Hedron";
import { BrandSplitView } from "src/library/components/BrandSplitView";
import { Button } from "src/library/components/Button";
import { Text } from "src/library/components/Text";
import { Logo } from "src/core/shared/components/Logo";
import {
  GetGlobalSettingsParamsType,
  IPlatformSetting,
  ISettingsResponse,
  useGlobalSettingsApi
} from "src/core/shared/api/globalSettings.api";
import type { IStore } from "src/core/shared/store";
import { PageProps } from "src/pages/Router/routes";

/**
 * 404 Not Found View
 *
 * @param {*} { history }
 * @returns
 */
const MaintenanceMode: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const [ settings, globalSettingsApi ] = useGlobalSettingsApi();
  const [ getSettingResponse, getSetting ] = globalSettingsApi<ISettingsResponse<IPlatformSetting>>("GET_SETTING");
  const [ allowAccess, setAllowAccess ] = React.useState<boolean>(false);
  const [ awaitingResponse, setAwaitingResponse ] = React.useState<boolean>(false);

  const { history, error } = useMappedState((store: IStore) => ({
    history: store.global.history,
    error: store.global.error
  }));

  const redirect = _.find(history, item => {
    return item.pathname !== props.location.pathname;
  });

  // On mount get the plaform settings to check if we are in maintenance mode
  React.useEffect(() => {
    if (settings && settings.platform.maintenance_mode.roles.length > 0) {
      setAwaitingResponse(true);
      getSetting<GetGlobalSettingsParamsType>("platform");
    }
  }, []);

  // Once the getSettings response has updated
  React.useEffect(() => {
    // Check if we are still in maintenance mode
    if (getSettingResponse.data && getSettingResponse.data.setting.maintenance_mode.roles.length === 0) {
      setAwaitingResponse(false);
      setAllowAccess(true);
    }
  }, [ getSettingResponse.complete ]);

  // Only redirect if allowed
  if (redirect && allowAccess) {
    return <Redirect to={redirect.pathname} />;
  }

  return (
    <BrandSplitView>
      <NotFoundInner>
        <Grid.Box className="logo-wrapper">
          <Logo type="dark" />
        </Grid.Box>

        <div className="padding-container">
          <Text
            weight="bold"
            fontSize="h2"
          >
            Maintenance
          </Text>
        </div>

        <Grid.Box>
          <Text
            color="secondary"
            fontSize="h4"
          >
            {error && error.message}
          </Text>
        </Grid.Box>

        <div className="padding-container">
          <div className="button-wrapper">
            <Button
              loading={awaitingResponse}
              onClick={() => {
                setAwaitingResponse(true);
                getSetting<GetGlobalSettingsParamsType>("platform");
              }}
            >
              Retry
            </Button>
          </div>

          <div className="or">
            <Text fontSize="h4">
              Or
            </Text>
          </div>

          <div className="button-wrapper">
            <Button
              onClick={() => {
                props.history.push("/logout");
              }}
            >
              Logout
            </Button>
          </div>
        </div>
      </NotFoundInner>
    </BrandSplitView>
  );
};

const NotFoundInner = styled(Grid.Bounds)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: ${({ theme }) => theme.spacing.padding.xl};
  padding-bottom: 0;

  .padding-container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding-top: ${({ theme }) => theme.spacing.padding.xl};

    .button-wrapper {
      .retry-button-wrapper {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        width: 65px;
      }
    }

    .or {
      padding-left: 12px;
      padding-right: 12px;
    }
  }


  .logo-wrapper {
    max-width: 400px;
  }
`;

export default MaintenanceMode;
